import { IRuntimeVO } from 'library/ts/interfaces/utilities/runtime/IRuntimeVO';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Background image loader
 */
export class BackgroundImage {
	#logger: ILogger;

	constructor(
		container: HTMLElement,
		imageURL: string,
		containerCSS: any = {
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'cover',
			backgroundPosition: 'center'
		}, // image scaling without black stripes - no black stripes, image may go out of bounds but covers entire area
		onSuccess: ( container: HTMLElement ) => void = undefined,
		onFailure: ( event: Event ) => void = undefined,
		utils: Utilities,
		runtimeVO: IRuntimeVO
	) {

		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', 'Skin:', this.name + ':' ] } );

		const backgroundImage = new Image();

		// image URL SSL conversion
		if ( runtimeVO?.top?.ssl ) {
			if ( utils.URL.valid( imageURL ) ) imageURL = utils.URL.toHTTPS( imageURL, true );
		}

		if ( utils.Type.isObject( containerCSS ) ) {
			Object.keys( containerCSS ).forEach(
				( key ) => {
					container.style[ key ] = containerCSS[ key ]; // apply style property
				}
			);
		}

		/**
		 * @description Background image onload event processor
		 * @param {Event} event
		 */
		backgroundImage.onload = ( event: Event ): void => {
			this.#logger.log( 'Image onload:', event );
			container.style.backgroundImage = 'url(' + backgroundImage.src + ')'; // set image background
			if ( utils.Type.isFunction( onSuccess ) ) onSuccess( container ); // execute success callback
		};

		/**
		 * @description Background image onerror event processor
		 * @param {Event} event
		 */
		backgroundImage.onerror = ( event: Event ): void => {
			this.#logger.warn( 'Image onerror:', event );
			if ( utils.Type.isFunction( onFailure ) ) onFailure( event ); // execute failure callback
		};

		this.#logger.log( 'Load Image:', imageURL );
		backgroundImage.src = imageURL; // load background image

	}

	get name(): string {
		return 'BackgroundImage';
	}

}